import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { RootState } from '../app/store'
import { logError } from '../utils/logger'
import { parseServiceFeePercentage, parseServicePrice } from '../utils'
import { TopBar } from '@/components/home'
import { fetchOrderDetails, fetchFullOrderDetails, cancelOrder, confirmPayment } from '../services/ordersService'
import apiService from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import CancelOrderModal from '../components/orders/CancelOrderModal'
import ConfirmPaymentModal from '../components/orders/ConfirmPaymentModal'
import EditOrderModal from '../components/orders/EditOrderModal'
import Receipt from '../components/receipt/Receipt'
import { X, Printer, CreditCard, ArrowLeft, ArrowRight, XCircle, Check, Pencil } from 'lucide-react'
import CurrencyDisplay from '../components/shared/CurrencyDisplay'
import VcardIcon from '../assets/images/Vcard.svg'
import RecardIcon from '../assets/images/Recard.svg'
import OrderIcon from '../assets/images/order.svg'
import RedDeleteIcon from '../assets/images/redDelete.svg'
import CashIcon from '../assets/images/cash.svg'
import OnlineIcon from '../assets/images/online.svg'
import DoIcon from '../assets/images/do.svg'
import RefreshIcon from '../assets/images/refresh.svg'
import CloseIcon from '../assets/images/close.svg'
import userIcon from '../assets/images/users.svg'
import partialIcon from '../assets/images/partial.svg'
import loIcon from '../assets/images/lo.svg'
import { Order, OrderStatus } from '@/types'
import {
  parseOrderPromotion,
  type OrderPromotion,
} from '../features/offers/orderPromotion'

interface PartialPayment {
  id: number
  amount: number
  paymentMethod: string
  paymentMethodText: string
  isSuccessful: boolean
  isWaiting: boolean
  transactionId?: string
}

interface OrderDetailData {
  id: number
  order_number?: string
  user: {
    id: number
    name: string
    phone: string
  }
  status: string
  statusText: string
  statusId: number
  typeId: number
  typeText: string
  paymentMethod: string
  paymentMethodText: string
  paymentStatus?: string
  paymentTransactionId?: string
  paymentDate?: string
  platform?: string
  branch: string
  branchId?: number
  creator?: string
  createdAt: string
  items: Array<{
    id: number
    item_id?: number
    product_id?: string | number
    quantity: number
    price?: number
    total_price?: number
    meals: Array<{
      id: number
      name: string
      price?: number
      image?: string
      additionals: Array<{
        id: number
        name: string
        price?: number
      }>
    }>
  }>
  amount: number
  taxValue: number
  taxPercentage?: string
  servicePrice: number
  serviceFeePercentage: number | null
  discount?: string
  deliveryPrice?: string
  table?: {
    id: number
    name: string
    tablePrice: string
    seatsNumber: number
    tableType: string
  }
  carDetails?: {
    carModel: string
    carColor: string
    carNumber: string
    carNotes?: string | null
  }
  address?: {
    id: number
    addressName: string
    typeText: string
    descriptiveAddress: string
  }
  deliveryStartTime?: string
  deliveryEndTime?: string
  deliveryDuration?: string
  partialPayments: PartialPayment[]
  hasRealPartialPayments: boolean
  remainingPaymentAmount: number
  finalTotal: number
  createdBy?: string | number
  notes?: string
  cobone?: {
    id: number
    code: string
  }
  promotion?: OrderPromotion
}

const OrderDetailsPage: React.FC = () => {
  const { t } = useTranslation()
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const returnTo =
    (location.state as { from?: string } | null)?.from || '/orders'
  const { settings } = useSelector((state: RootState) => state.settings)
  const { user } = useSelector((state: RootState) => state.auth)
  const isCustomerServiceUser =
    user?.type === 'customer_service' || user?.user_type === 'customer_service'
  const csSelectedBranchId = useSelector((state: RootState) => state.csFlow.selectedBranchId)

  // Current branch id - priority: CS selected branch > auth user branch > localStorage employee_data
  const currentBranchId = useMemo(() => {
    if (csSelectedBranchId) {
      const csId = Number(csSelectedBranchId)
      if (!isNaN(csId) && csId > 0) return csId
    }
    if (user?.branch?.id) {
      const id = typeof user.branch.id === 'number' ? user.branch.id : Number(user.branch.id)
      if (!isNaN(id) && id > 0) return id
    }
    try {
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        if (employeeData.branch?.id) {
          const id = typeof employeeData.branch.id === 'number'
            ? employeeData.branch.id
            : Number(employeeData.branch.id)
          if (!isNaN(id) && id > 0) return id
        }
      }
    } catch (e) {
      return null
    }
    return null
  }, [csSelectedBranchId, user])

  // Current branch name - the order detail API returns branch only as a name string,
  // so matching is done by name (fallback for id-based matching when branch_id exists)
  const currentBranchName = useMemo(() => {
    const normalize = (value: unknown): string =>
      value ? String(value).trim() : ''

    if (user?.branch?.name) return normalize(user.branch.name)
    try {
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        if (employeeData.branch?.name) return normalize(employeeData.branch.name)
      }
    } catch (e) {
      /* ignore */
    }
    try {
      const branchDataStr = localStorage.getItem('selected_branch_data')
      if (branchDataStr) {
        const branchData = JSON.parse(branchDataStr)
        if (branchData.name) return normalize(branchData.name)
      }
    } catch (e) {
      /* ignore */
    }
    return ''
  }, [user])

  const [orderDetail, setOrderDetail] = useState<OrderDetailData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [isCanceling, setIsCanceling] = useState(false)
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false)
  const [isConfirmPaymentModalOpen, setIsConfirmPaymentModalOpen] = useState(false)
  const [isEditOrderModalOpen, setIsEditOrderModalOpen] = useState(false)

  const isRTL = settings.language === 'ar'

  // Fetch order details on component mount
  useEffect(() => {
    const loadOrderDetails = async () => {
      if (!orderId) {
        setErrorMessage(t('orders.order_not_found') || t('common.error') || 'Order not found')
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setErrorMessage(null)
      try {
        const mappedData = await fetchAndMapOrderDetail(orderId)
        setOrderDetail(mappedData)
      } catch (err: any) {
        setErrorMessage(err?.message || t('orders.order_not_found') || t('common.error') || 'Error loading order details')
      } finally {
        setIsLoading(false)
      }
    }

    loadOrderDetails()
  }, [orderId, t])

  // Map API response to OrderDetailData
  const mapOrderDetailData = (data: any): OrderDetailData => {
    // Calculate item total helper
    const calculateItemTotal = (item: any): number => {
      const mealPrice = item.meals?.[0]?.price || 0
      const additionalsPrice = item.meals?.reduce((sum: number, meal: any) => {
        return sum + (meal.additionals?.reduce((addSum: number, add: any) => addSum + (add.price || 0), 0) || 0)
      }, 0) || 0
      return (mealPrice + additionalsPrice) * item.quantity
    }

    // Format date and time
    const createdAt = data.created_at || data.createdAt || new Date().toISOString()
    const date = new Date(createdAt)
    const dateStr = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
    const hour = date.getHours() > 12 ? date.getHours() - 12 : (date.getHours() === 0 ? 12 : date.getHours())
    const period = date.getHours() >= 12 ? t('orders.time_period.pm') : t('orders.time_period.am')
    const minute = date.getMinutes().toString().padStart(2, '0')
    const timeStr = `${hour}:${minute} ${period}`

    // Map partial payments
    const partialPayments: PartialPayment[] = (data.partial_payments || data.partialPayments || []).map((payment: any) => {
      const amount = typeof payment.amount === 'string' ? parseFloat(payment.amount) : Number(payment.amount || 0)
      // Check both is_success and is_successful (API might use either)
      const isSuccess = payment.is_success === 1 || payment.is_success === true || 
                       payment.is_successful === 1 || payment.is_successful === true || 
                       payment.isSuccessful === true
      const isWaiting = payment.is_waiting === 1 || payment.is_waiting === true || 
                       payment.isWaiting === true
      return {
        id: payment.id || 0,
        amount: isNaN(amount) ? 0 : amount,
        paymentMethod: payment.payment_method || payment.paymentMethod || 'cash',
        paymentMethodText: payment.payment_method_text || payment.paymentMethodText || t('payment.cash') || 'Cash',
        isSuccessful: isSuccess,
        isWaiting: isWaiting,
        transactionId: payment.transaction_id || payment.transactionId,
      }
    })

    // Calculate remaining payment amount
    // Use remainingPaymentAmount from API if available, otherwise calculate from partial payments
    const remainingPaymentAmountStr = data.remainingPaymentAmount || data.remaining_payment_amount || data.remainingPayment || '0'
    const remainingPaymentAmountFromAPI = typeof remainingPaymentAmountStr === 'string' ? parseFloat(remainingPaymentAmountStr) : Number(remainingPaymentAmountStr || 0)
    
    const totalAmountStr = data.amount || data.total_amount || data.totalAmount || '0'
    const totalAmount = typeof totalAmountStr === 'string' ? parseFloat(totalAmountStr) : Number(totalAmountStr || 0)
    const totalAmountNum = isNaN(totalAmount) ? 0 : totalAmount
    
    const paidAmount = partialPayments.reduce((sum, p) => sum + (p.isSuccessful ? p.amount : 0), 0)
    const calculatedRemainingAmount = Math.max(0, totalAmountNum - paidAmount)
    
    // Use API value if available and valid, otherwise use calculated value
    const remainingAmount = (!isNaN(remainingPaymentAmountFromAPI) && remainingPaymentAmountFromAPI >= 0)
      ? remainingPaymentAmountFromAPI
      : calculatedRemainingAmount
    
    const hasRealPartialPayments = partialPayments.length > 0 && partialPayments.some(p => p.isSuccessful)

    // Use price_before_discount and total_price from API if available
    const priceBeforeDiscountStr = data.price_before_discount || data.priceBeforeDiscount || data.amount || data.subtotal || data.sub_total || '0'
    const priceBeforeDiscount = typeof priceBeforeDiscountStr === 'string' ? parseFloat(priceBeforeDiscountStr) : Number(priceBeforeDiscountStr || 0)
    const subtotalNum = isNaN(priceBeforeDiscount) ? 0 : priceBeforeDiscount
    
    const taxValueStr = data.tax_value || data.taxValue || data.tax_amount || data.taxAmount || '0'
    const taxValue = typeof taxValueStr === 'string' ? parseFloat(taxValueStr) : Number(taxValueStr || 0)
    const taxValueNum = isNaN(taxValue) ? 0 : taxValue
    
    const discountStr = data.discount || '0'
    const discount = typeof discountStr === 'string' ? parseFloat(discountStr) : Number(discountStr || 0)
    const discountNum = isNaN(discount) ? 0 : discount
    
    const deliveryPriceStr = data.delivery_price || data.deliveryPrice || '0'
    const deliveryPrice = typeof deliveryPriceStr === 'string' ? parseFloat(deliveryPriceStr) : Number(deliveryPriceStr || 0)
    const deliveryPriceNum = isNaN(deliveryPrice) ? 0 : deliveryPrice
    
    const tablePrice = data.table ? (() => {
      const tablePriceStr = data.table.table_price || data.table.tablePrice || '0'
      return typeof tablePriceStr === 'string' ? parseFloat(tablePriceStr) : Number(tablePriceStr || 0)
    })() : 0
    const tablePriceNum = isNaN(tablePrice) ? 0 : tablePrice
    const servicePriceNum = parseServicePrice(data)
    
    // Use total_price from API if available, otherwise calculate
    const totalPriceStr = data.total_price || data.totalPrice || data.total_amount || data.totalAmount || '0'
    const totalPriceFromAPI = typeof totalPriceStr === 'string' ? parseFloat(totalPriceStr) : Number(totalPriceStr || 0)
    const finalTotal = !isNaN(totalPriceFromAPI) && totalPriceFromAPI > 0
      ? totalPriceFromAPI
      : (subtotalNum + taxValueNum + deliveryPriceNum + tablePriceNum + servicePriceNum - discountNum)

    return {
      id: data.id,
      order_number: data.order_number || data.orderNumber || String(data.id),
      user: {
        id: data.user?.id || data.customer?.id || 0,
        name: data.user?.name || data.customer?.name || data.customer_name || '',
        phone: data.user?.phone || data.customer?.phone || data.customer_phone || data.phone || '',
      },
      status: data.status || '',
      statusText: data.status_text || data.statusText || '',
      statusId: Number(data.status_id ?? data.statusId ?? 0),
      typeId: data.type_id || data.typeId || 0,
      typeText: data.type_text || data.typeText || '',
      paymentMethod: data.payment_method || data.paymentMethod || '',
      paymentMethodText: data.payment_method_text || data.paymentMethodText || '',
      paymentStatus: data.payment_status || data.paymentStatus,
      paymentTransactionId: data.payment_transaction_id || data.paymentTransactionId || data.transaction_id || data.transactionId,
      paymentDate: data.payment_date || data.paymentDate,
      platform: data.platform,
      branch: typeof data.branch === 'string' 
        ? data.branch 
        : (data.branch && typeof data.branch === 'object' && data.branch !== null)
          ? ('name' in data.branch ? String(data.branch.name || '') : String(data.branch))
          : '',
      branchId: Number(data.branch_id ?? (data.branch && typeof data.branch === 'object' && data.branch !== null ? (data.branch as any).id : undefined)) || undefined,
      createdAt: createdAt,
      items: (data.items || []).map((item: any) => {
        // Use total_unit_price from API if available, otherwise fallback to total_price or calculate
        const itemTotalUnitPrice = typeof item.total_unit_price === 'string' 
          ? parseFloat(item.total_unit_price) 
          : (typeof item.total_unit_price === 'number' ? item.total_unit_price : 0)
        
        const itemPrice = typeof item.price === 'string' ? parseFloat(item.price) : Number(item.price || 0)
        const itemTotalPrice = typeof item.total_price === 'string' ? parseFloat(item.total_price) : Number(item.total_price || 0)
        const itemQuantity = typeof item.quantity === 'string' ? parseInt(item.quantity) : Number(item.quantity || 1)
        
        // Use total_unit_price from API as the primary source for item total
        const finalTotalPrice = !isNaN(itemTotalUnitPrice) && itemTotalUnitPrice > 0
          ? itemTotalUnitPrice
          : (!isNaN(itemTotalPrice) && itemTotalPrice > 0
            ? itemTotalPrice
            : (!isNaN(itemPrice) && itemPrice > 0
              ? itemPrice * (isNaN(itemQuantity) ? 1 : itemQuantity)
              : 0))
        
        return {
          id: item.id,
          item_id: item.item_id != null ? Number(item.item_id) : undefined,
          product_id: item.product_id || item.product?.id || item.meals?.[0]?.id || item.id,
          quantity: isNaN(itemQuantity) ? 1 : itemQuantity,
          price: isNaN(itemPrice) ? 0 : itemPrice,
          total_price: finalTotalPrice,
          meals: (item.meals || []).map((meal: any) => {
            const mealPrice = typeof meal.price === 'string' ? parseFloat(meal.price) : Number(meal.price || 0)
            const mealName = typeof meal.name === 'string' 
              ? meal.name 
              : (meal.name && typeof meal.name === 'object' && 'name' in meal.name)
                ? String(meal.name.name || '')
                : String(meal.name || '')
            return {
              id: meal.id,
              name: mealName,
              price: isNaN(mealPrice) ? 0 : mealPrice,
              image: meal.image || '',
              additionals: (meal.additionals || []).map((add: any) => {
                const addPrice = typeof add.price === 'string' ? parseFloat(add.price) : Number(add.price || 0)
                const addName = typeof add.name === 'string'
                  ? add.name
                  : (add.name && typeof add.name === 'object' && 'name' in add.name)
                    ? String(add.name.name || '')
                    : String(add.name || '')
                return {
                  id: add.id,
                  name: addName,
                  price: isNaN(addPrice) ? 0 : addPrice,
                }
              }),
            }
          }),
        }
      }),
      creator: data.creator || data.created_by_name || data.cashier?.name || '',
      createdBy: data.created_by || data.createdBy || data.creator_id || data.cashier?.id,
      amount: subtotalNum,
      taxValue: taxValueNum,
      taxPercentage: data.tax_percentage || data.taxPercentage,
      servicePrice: servicePriceNum,
      serviceFeePercentage: parseServiceFeePercentage(data),
      discount: (discountNum > 0) ? String(discountNum) : undefined,
      // Always include deliveryPrice if type_id is 1 (delivery), even if zero
      // Will be filtered in UI based on type_id
      deliveryPrice: String(deliveryPriceNum),
      // Show table if it exists and has id, regardless of table_price value (will be filtered by type_id in UI)
      table: (data.table && !Array.isArray(data.table) && data.table.id) ? {
        id: data.table.id,
        name: data.table.name || data.table.number || '',
        tablePrice: String(tablePriceNum),
        seatsNumber: data.table.seats_number || data.table.seatsNumber || 0,
        tableType: data.table.table_type || data.table.tableType || '',
      } : undefined,
      carDetails: (data.carDetails || data.car_details) ? {
        carModel: data.carDetails?.carModel || data.car_details?.carModel || data.carDetails?.car_model || data.car_details?.car_model || '',
        carColor: data.carDetails?.carColor || data.car_details?.carColor || data.carDetails?.car_color || data.car_details?.car_color || '',
        carNumber: data.carDetails?.carNumber || data.car_details?.carNumber || data.carDetails?.car_number || data.car_details?.car_number || '',
        carNotes: data.carDetails?.carNotes || data.car_details?.carNotes || data.carDetails?.car_notes || data.car_details?.car_notes || null,
      } : undefined,
      address: (data.address || data.user?.address) ? (() => {
        const addressData = data.address || data.user?.address
        return {
          id: addressData.id,
          addressName: addressData.address_name || addressData.addressName || '',
          typeText: addressData.type_text || addressData.typeText || addressData.type || '',
          descriptiveAddress: addressData.descriptive_address || addressData.descriptiveAddress || '',
        }
      })() : undefined,
      deliveryStartTime: data.delivery_start_time || data.deliveryStartTime,
      deliveryEndTime: data.delivery_end_time || data.deliveryEndTime,
      deliveryDuration: data.delivery_duration || data.deliveryDuration,
      partialPayments: partialPayments,
      hasRealPartialPayments: hasRealPartialPayments,
      remainingPaymentAmount: remainingAmount,
      finalTotal: finalTotal,
      notes: data.notes || data.order_notes || '',
      cobone: data.cobone || data.coupon,
      promotion: parseOrderPromotion(data.promotion),
    }
  }

  const fetchAndMapOrderDetail = async (targetOrderId?: string | number): Promise<OrderDetailData> => {
    const idToUse = targetOrderId ?? orderId
    if (!idToUse) {
      throw new Error(t('orders.order_not_found') || t('common.error') || 'Order not found')
    }

    const fullOrderData = await fetchFullOrderDetails(idToUse)
    return mapOrderDetailData(fullOrderData)
  }

  // Calculate item total - use total_price from mapped data (which uses total_unit_price from API)
  const calculateItemTotal = (item: any): number => {
    if (!orderDetail || !item) return 0
    
    try {
      // Use total_price from mapped data (which already uses total_unit_price from API)
      if (item.total_price !== undefined && item.total_price !== null) {
        const totalPrice = typeof item.total_price === 'string' ? parseFloat(item.total_price) : Number(item.total_price)
        if (!isNaN(totalPrice) && totalPrice > 0) {
          return totalPrice
        }
      }
      
      // Fallback: use price * quantity
      if (item.price !== undefined && item.price !== null) {
        const price = typeof item.price === 'string' ? parseFloat(item.price) : Number(item.price)
        const quantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) : Number(item.quantity)
        if (!isNaN(price) && !isNaN(quantity)) {
          return price * quantity
        }
      }
      
      // Last fallback: calculate from meals and additionals
      const mealPrice = item.meals?.[0]?.price 
        ? (typeof item.meals[0].price === 'string' ? parseFloat(item.meals[0].price) : Number(item.meals[0].price))
        : 0
      
      const additionalsPrice = item.meals?.reduce((sum: number, meal: any) => {
        const mealAddPrice = meal.additionals?.reduce((addSum: number, add: any) => {
          const addPrice = add.price 
            ? (typeof add.price === 'string' ? parseFloat(add.price) : Number(add.price))
            : 0
          return addSum + (isNaN(addPrice) ? 0 : addPrice)
        }, 0) || 0
        return sum + (isNaN(mealAddPrice) ? 0 : mealAddPrice)
      }, 0) || 0
      
      const quantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) : Number(item.quantity)
      const qty = isNaN(quantity) ? 1 : quantity
      
      const mealPriceNum = isNaN(mealPrice) ? 0 : mealPrice
      const additionalsPriceNum = isNaN(additionalsPrice) ? 0 : additionalsPrice
      
      return (mealPriceNum + additionalsPriceNum) * qty
    } catch (error) {
      return 0
    }
  }

  // Button visibility helpers
  const shouldShowCompletePaymentButton = (): boolean => {
    if (isCustomerServiceUser) return false
    if (!orderDetail) return false
    
    // Show button if:
    // 1. Payment method is partial
    // 2. Payment status is waiting_payment
    // 3. There is remaining amount to pay (more than 0.01)
    const isPartialPayment = orderDetail.paymentMethod === 'partial'
    const isWaitingPayment = orderDetail.paymentStatus === 'waiting_payment'
    const hasRemainingAmount = orderDetail.remainingPaymentAmount > 0.01
    
    // Show button for partial payment orders with remaining amount
    return isPartialPayment && isWaitingPayment && hasRemainingAmount
  }

  const shouldShowPrintButton = (): boolean => {
    if (isCustomerServiceUser) return false
    return true
  }

  const shouldShowConfirmPaymentButton = (): boolean => {
    if (isCustomerServiceUser) return false
    if (!orderDetail) return false
    if (Number(orderDetail.typeId) === 1) return false
    // Don't show if there are partial payments (use complete payment instead)
    if (orderDetail.partialPayments.length > 0) return false

    const paymentStatusLower = orderDetail.paymentStatus?.toLowerCase()
    const paymentMethodLower = orderDetail.paymentMethod?.toLowerCase()

    return (
      paymentStatusLower === 'waiting_payment' &&
      paymentMethodLower === 'cash'
    )
  }

  const shouldShowPartialPaymentButton = (): boolean => {
    if (!orderDetail) return false
    // This button should not show if complete payment button is showing
    // Cancel order button logic
    const hasPayments = orderDetail.partialPayments.length > 0
    const hasTransactionId = orderDetail.paymentTransactionId !== null && 
                            orderDetail.paymentTransactionId !== undefined && 
                            orderDetail.paymentTransactionId !== ''
    
    if (orderDetail.paymentStatus === 'paid' && orderDetail.paymentMethod === 'cash') {
      return false
    }
    
    // Don't show cancel button if there are partial payments (use complete payment instead)
    if (hasPayments || hasTransactionId) {
      return false
    }
    
    return orderDetail.paymentStatus === 'waiting_payment' &&
           !hasTransactionId &&
           orderDetail.paymentMethod === 'partial' &&
           orderDetail.platform === 'cashier'
  }

  // Action handlers
  const handleBack = () => {
    navigate(returnTo)
  }

  const handleCompletePayment = () => {
    if (!orderDetail) return
    navigate(`/partial-payment/${orderDetail.id}`)
  }

  const handlePrintOrder = () => {
    if (!orderDetail) {
      toast.error(t('orders.no_order_data_to_print') || 'لا توجد بيانات طلب للطباعة')
      return
    }

    try {
      // Wait a bit to ensure the receipt is rendered in the DOM
      setTimeout(() => {
        // Find the receipt container
        const receiptContainer = document.querySelector('.receipt-container') as HTMLElement
        
        if (receiptContainer) {
          // Just trigger print - CSS will handle showing the receipt in print mode
          // No need to change classes, @media print will handle visibility
          setTimeout(() => {
            // Trigger print dialog
            window.print()
          }, 100)
        } else {
          // If receipt container not found, show error
          toast.error(t('orders.receipt_not_found') || 'لم يتم العثور على الفاتورة للطباعة')
          logError('Receipt container not found in DOM')
        }
      }, 200)
    } catch (err: any) {
      toast.error(t('orders.print_error') || 'حدث خطأ أثناء طباعة الفاتورة')
      logError('Print error:', err)
    }
  }

  const handleRefresh = () => {
    if (!orderId) return
    setIsLoading(true)
    setErrorMessage(null)
    fetchAndMapOrderDetail(orderId)
      .then((mappedData) => {
        setOrderDetail(mappedData)
      })
      .catch((err) => {
        setErrorMessage(err?.message || t('common.error') || 'Error updating data')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  // Lightweight refresh used after order edits (keeps the page visible)
  const refreshOrderDetail = async () => {
    if (!orderId) return
    const mappedData = await fetchAndMapOrderDetail(orderId)
    setOrderDetail(mappedData)
  }

  const handleConfirmPayment = () => {
    if (!orderDetail) return
    setIsConfirmPaymentModalOpen(true)
  }

  const handleConfirmPaymentConfirm = async () => {
    if (!orderDetail) return
    setIsConfirmingPayment(true)
    try {
      await confirmPayment(orderDetail.id)
      const refreshedData = await fetchAndMapOrderDetail(orderDetail.id)
      setOrderDetail(refreshedData)
      setIsConfirmPaymentModalOpen(false)
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        t('orders.confirm_payment_error')
      alert(errorMsg)
    } finally {
      setIsConfirmingPayment(false)
    }
  }

  const handleCancelOrder = () => {
    if (!orderDetail) return
    setIsCancelModalOpen(true)
  }

  const handleConfirmCancel = async () => {
    if (!orderDetail) return
    
    setIsCanceling(true)
    try {
      await cancelOrder(orderDetail.id)
      // Return to where the user came from (e.g. collection/payment pages)
      navigate(returnTo)
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.descriptiveMessage ||
        err?.response?.data?.message ||
        err?.message ||
        t('orders.cancel_order_error')
      alert(errorMsg)
      setIsCanceling(false)
      setIsCancelModalOpen(false)
    }
  }

  // Format date and time
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    const dateStr = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
    const hour = date.getHours() > 12 ? date.getHours() - 12 : (date.getHours() === 0 ? 12 : date.getHours())
    const period = date.getHours() >= 12 ? t('orders.time_period.pm') : t('orders.time_period.am')
    const minute = date.getMinutes().toString().padStart(2, '0')
    return { date: dateStr, time: `${hour}:${minute} ${period}` }
  }

  // Get payment method icon
  const getPaymentMethodIcon = (method: string): string => {
    const methodLower = method.toLowerCase()
    if (methodLower.includes('cash') || methodLower.includes('كاش')) {
      return CashIcon
    }
    if (methodLower.includes('wallet') || methodLower.includes('online') || methodLower.includes('network')) {
      return OnlineIcon
    }
    return CashIcon
  }

  // Get payment status icon and color
  const getPaymentStatusIcon = (payment: PartialPayment): { icon: string; color: string } => {
    // If payment is successful, show green checkmark
    if (payment.isSuccessful) {
      return { icon: DoIcon, color: '#10b981' } // green
    } else if (payment.isWaiting) {
      return { icon: RefreshIcon, color: '#f97316' } // orange
    } else {
      return { icon: CloseIcon, color: '#ef4444' } // red
    }
  }

  const getPaymentStatusText = (payment: PartialPayment): string => {
    if (payment.isSuccessful) {
      return t('orders.paid') || 'تم الدفع'
    } else if (payment.isWaiting) {
      return t('orders.waiting_payment') || 'في انتظار الدفع'
    } else {
      return t('orders.failed') || 'فشل'
    }
  }

  // Error state
  if (errorMessage) {
    return (
      <div className="w-full h-screen bg-neutral-100 flex flex-col">
        <TopBar userName={user?.name} />
        <div className="flex-1 flex flex-col justify-center items-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <p className="text-red-500 text-lg font-medium mb-4">{errorMessage}</p>
          <button
            onClick={handleRefresh}
            className="px-6 py-2 bg-flutter-button rounded-lg text-white font-medium"
          >
            {t('common.retry') || 'إعادة المحاولة'}
          </button>
        </div>
      </div>
    )
  }

  // Loading state
  if (isLoading || !orderDetail) {
    return (
      <div className="w-full h-screen bg-neutral-100 flex flex-col">
        <TopBar userName={user?.name} />
        <div className="flex-1 flex justify-center items-center">
          <div className="w-16 h-16 bg-white rounded-2xl flex justify-center items-center shadow-md">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    )
  }

  const { date, time } = formatDateTime(orderDetail.createdAt)

  // Get status configuration similar to OrderCardNew
  // Uses statusId first (most reliable), then falls back to status string matching
  const getStatusConfig = (status: string, statusLabel?: string, statusId?: number) => {
    // First, try to determine status from statusId (most reliable)
    let determinedStatus: 'completed' | 'pending' | 'preparing' | 'cancelled' | null = null
    
    if (statusId !== undefined && statusId !== null && statusId !== 0) {
      switch (statusId) {
        case 1:
        case 2:
        case 3:
          determinedStatus = 'preparing'
          break
        case 4:
          determinedStatus = 'completed'
          break
        case 6:
        case 9:
          determinedStatus = 'pending'
          break
        default:
          // Keep as null to fall back to string matching
          break
      }
    }
    
    // Use statusLabel from API if available (it might already be translated)
    // Otherwise, use translation keys based on status
    let label: string
    if (statusLabel) {
      // Check if statusLabel is a translation key (starts with 'orders.')
      if (statusLabel.startsWith('orders.status.') || statusLabel.startsWith('orders.')) {
        // It's a translation key
        label = t(statusLabel)
      } else {
        // It's an API-provided translated label, use it directly
        label = statusLabel
      }
    } else {
      // No statusLabel, determine from statusId or status string and translate
      if (determinedStatus === 'completed') {
        label = t('orders.status.completed')
      } else if (determinedStatus === 'preparing') {
        label = t('orders.status.active')
      } else if (determinedStatus === 'pending') {
        label = t('orders.status.pending')
      } else {
        // Fall back to string matching
        const statusLower = status.toLowerCase()
        if (statusLower.includes('completed') || statusLower.includes('تم') || statusLower.includes('done')) {
          label = t('orders.status.completed')
        } else if (statusLower.includes('preparing') || statusLower.includes('ready') || statusLower.includes('نشط')) {
          label = t('orders.status.active')
        } else if (statusLower.includes('pending') || statusLower.includes('معلق')) {
          label = t('orders.status.pending')
        } else if (statusLower.includes('cancelled') || statusLower.includes('ملغي')) {
          label = t('orders.status.cancelled')
        } else {
          label = t('orders.status.active')
        }
      }
    }

    // Determine icon and styling based on statusId first, then fall back to string matching
    if (determinedStatus === 'completed') {
      return {
        label,
        icon: DoIcon,
        bgColor: 'bg-neutral-100',
        textColor: 'text-black',
      }
    }
    if (determinedStatus === 'preparing') {
      return {
        label,
        icon: partialIcon,
        bgColor: 'bg-neutral-100',
        textColor: 'text-black',
      }
    }
    if (determinedStatus === 'pending') {
      return {
        label,
        icon: loIcon,
        bgColor: 'bg-neutral-100',
        textColor: 'text-black',
      }
    }
    
    // Fall back to string matching if statusId didn't match
    const statusLower = status.toLowerCase()
    if (statusLower.includes('completed') || statusLower.includes('تم') || statusLower.includes('done')) {
      return {
        label,
        icon: DoIcon,
        bgColor: 'bg-neutral-100',
        textColor: 'text-black',
      }
    }
    if (statusLower.includes('preparing') || statusLower.includes('ready') || statusLower.includes('نشط')) {
      return {
        label,
        icon: partialIcon,
        bgColor: 'bg-neutral-100',
        textColor: 'text-black',
      }
    }
    if (statusLower.includes('pending') || statusLower.includes('معلق')) {
      return {
        label,
        icon: loIcon,
        bgColor: 'bg-neutral-100',
        textColor: 'text-black',
      }
    }
    if (statusLower.includes('cancelled') || statusLower.includes('ملغي')) {
      return {
        label,
        icon: XCircle,
        bgColor: 'bg-neutral-100',
        textColor: 'text-black',
      }
    }
    return {
      label,
      icon: partialIcon,
      bgColor: 'bg-neutral-100',
      textColor: 'text-black',
    }
  }

  const statusConfig = getStatusConfig(orderDetail.status, orderDetail.statusText, orderDetail.statusId)
  const StatusIcon = statusConfig.icon

  // Helper function to safely extract branch name
  const getBranchName = (): string => {
    if (!orderDetail.branch) return ''
    if (typeof orderDetail.branch === 'string') return orderDetail.branch
    if (typeof orderDetail.branch === 'object' && orderDetail.branch !== null && 'name' in orderDetail.branch) {
      return String((orderDetail.branch as any).name || '')
    }
    return String(orderDetail.branch || '')
  }

  // Order belongs to the current cashier branch - matched by id when the API
  // provides branch_id, otherwise by branch name (the order detail API returns
  // branch only as a name string, e.g. "Stanli branch")
  const isOrderInCurrentBranch = (() => {
    const orderBranchId =
      orderDetail.branchId != null ? Number(orderDetail.branchId) : null
    if (orderBranchId != null && currentBranchId != null && orderBranchId === Number(currentBranchId)) {
      return true
    }
    const orderBranchName = getBranchName().trim().toLowerCase()
    const activeBranchName = currentBranchName.toLowerCase()
    return orderBranchName !== '' && activeBranchName !== '' && orderBranchName === activeBranchName
  })()

  // Any cashier in the order's branch may edit it, regardless of who created it
  const canEditOrder =
    Number(orderDetail.statusId) === 1 &&
    Number(orderDetail.typeId) === 3 &&
    orderDetail.paymentStatus?.toLowerCase() !== 'paid' &&
    (isCustomerServiceUser || isOrderInCurrentBranch)

  const canCancelDineInOrder =
    Number(orderDetail.typeId) === 3 &&
    [1, 2, 3].includes(Number(orderDetail.statusId)) &&
    orderDetail.paymentStatus?.toLowerCase() !== 'paid' &&
    orderDetail.partialPayments.length === 0 &&
    !orderDetail.paymentTransactionId &&
    (isCustomerServiceUser || isOrderInCurrentBranch)

  // Helper function to format platform name
  const getPlatformLabel = (platform?: string): string => {
    if (!platform) return ''
    const platformLower = platform.toLowerCase()
    if (platformLower === 'ios') return t('orders.platform.ios')
    if (platformLower === 'android') return t('orders.platform.android')
    if (platformLower === 'web') return t('orders.platform.web')
    if (platformLower === 'pos') return t('orders.platform.pos')
    if (platformLower === 'cashier') return t('orders.platform.cashier')
    if (platformLower === 'qr') return t('orders.platform.qr')
    if (platformLower === 'customer_service') return t('orders.platform.customer_service')
    return platform
  }

  const getPaymentMethodLabel = (method?: string): string => {
    if (!method) return ''
    const methodLower = method.toLowerCase()
    if (methodLower.includes('cash') || methodLower.includes('كاش')) return t('payment.cash')
    if (methodLower.includes('card') || methodLower.includes('شبكة') || methodLower.includes('network')) return t('payment.network')
    if (methodLower.includes('online')) return t('payment.online')
    if (methodLower.includes('partial')) return t('payment.partial')
    if (methodLower.includes('wallet') || methodLower.includes('محفظة')) return t('payment.wallet')
    return method
  }

  // Helper function to get type_id label using translations
  const getTypeIdLabel = (typeId?: number): string => {
    if (!typeId) return ''
    const typeMap: Record<number, string> = {
      1: t('orders.type.delivery'),
      2: t('orders.type.takeaway'),
      3: t('orders.type.dine_in'),
      4: t('orders.type.car_pickup'),
    }
    return typeMap[typeId] || String(typeId)
  }

  // Build order details array similar to OrderCardNew
  const platformLabel = orderDetail.platform ? getPlatformLabel(orderDetail.platform) : ''
  const paymentMethodLabel = orderDetail.paymentMethodText || getPaymentMethodLabel(orderDetail.paymentMethod)

  const orderDetails = [
    orderDetail.creator || '',
    getBranchName(),
    orderDetail.typeText || getTypeIdLabel(orderDetail.typeId),
    platformLabel,
    paymentMethodLabel,
    orderDetail.order_number ? `#${orderDetail.order_number}` : '',
    `${time} ${date}`,
    `${orderDetail.items.length} ${t('orders.items_count_label')}`,
  ].filter((item): item is string => typeof item === 'string' && item !== '')

  const isPaidWithoutPartialPayments =
    orderDetail.partialPayments.length === 0 &&
    orderDetail.paymentStatus?.toLowerCase() === 'paid'

  // Check if we should always show payment card for cash + waiting_payment (for cashier and CS)
  const isCashWaitingPayment = 
    orderDetail.paymentMethod?.toLowerCase() === 'cash' &&
    orderDetail.paymentStatus?.toLowerCase() === 'waiting_payment'

  const fallbackPaymentAmount = Number(orderDetail.finalTotal || orderDetail.amount || 0)
  const fallbackPaymentMethod = orderDetail.paymentMethod || 'cash'
  const fallbackPaymentMethodText = orderDetail.paymentMethodText || getPaymentMethodLabel(fallbackPaymentMethod)
  const fallbackPayment: PartialPayment = {
    id: 0,
    amount: fallbackPaymentAmount,
    paymentMethod: fallbackPaymentMethod,
    paymentMethodText: fallbackPaymentMethodText,
    isSuccessful: isPaidWithoutPartialPayments,
    isWaiting: isCashWaitingPayment && !isPaidWithoutPartialPayments,
  }

  const paymentDateInfo = orderDetail.paymentDate ? formatDateTime(orderDetail.paymentDate) : null
  const paymentIdentifierLabel = orderDetail.paymentTransactionId
    ? (t('orders.payment_id') || 'رقم الدفع')
    : (t('orders.order_number') || 'رقم الطلب')
  const paymentIdentifierValue = orderDetail.paymentTransactionId || orderDetail.id
  // Always show payment info if: there are partial payments, or paid without partial payments, or cash + waiting_payment
  const shouldShowPaymentInfo = orderDetail.partialPayments.length > 0 || isPaidWithoutPartialPayments || isCashWaitingPayment
  const fallbackPaymentStatusInfo = getPaymentStatusIcon(fallbackPayment)
  const fallbackPaymentStatusText = getPaymentStatusText(fallbackPayment)

  return (
    <div className="w-full h-screen bg-neutral-100 flex flex-col overflow-hidden">
      {/* Top Bar */}
         
         {/* Top Bar */}
         <div className={` pt-2 ${isRTL ? 'pr-0 pl-4' : 'pl-0 pr-4'}`}>
         <TopBar userName={user?.name}/>
         </div>
      {/* Fixed Header - Back Button and Action Buttons Row */}
      <div className="px-4 pt-2 pb-2 flex-shrink-0 bg-neutral-100">
        <div className="flex justify-between items-center gap-2">
          {/* Left Back Button */}
          <button
            onClick={handleBack}
            className="w-10 h-10 bg-white rounded-xl flex justify-center items-center shadow-sm hover:shadow-md transition-shadow"
          >
            {isRTL ? <ArrowRight className="w-5 h-5 text-icon-primary" /> : <ArrowLeft className="w-5 h-5 text-icon-primary" />}
          </button>

          <div className="flex items-center gap-3 flex-wrap">
            {shouldShowCompletePaymentButton() && (
              <button
                onClick={handleCompletePayment}
                className={`h-10 px-3 py-1 bg-white rounded-lg flex items-center gap-2 shadow-sm hover:shadow-md transition-shadow ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <img src={VcardIcon} alt="complete payment" className="w-5 h-5" />
                <span className="text-sm font-medium font-['IBM Plex Sans Arabic'] text-text-primary">
                  {t('orders.complete_payment') || 'استكمال الدفع'}
                </span>
              </button>
            )}

            {shouldShowPrintButton() && (
              <button
                onClick={handlePrintOrder}
                className={`h-10 px-3 py-1 bg-white rounded-lg flex items-center gap-2 shadow-sm hover:shadow-md transition-shadow flex-row-reverse`}
              >
                <span className="text-sm font-medium font-['IBM Plex Sans Arabic'] text-text-primary">
                  {t('orders.print_order') || 'طباعة الطلب'}
                </span>
                <img src={OrderIcon} alt="print" className="w-5 h-5" />

              </button>
            )}

            {canEditOrder && (
              <button
                onClick={() => setIsEditOrderModalOpen(true)}
                className={`h-10 px-3 py-1 bg-white rounded-lg flex items-center gap-2 shadow-sm hover:shadow-md transition-shadow flex-row-reverse`}
              >
                <span className="text-sm font-medium font-['IBM Plex Sans Arabic'] text-text-primary">
                  {t('orders.edit_order') || 'تعديل الطلب'}
                </span>
                <Pencil className="w-5 h-5 text-icon-primary" />
              </button>
            )}

            {shouldShowConfirmPaymentButton() && isOrderInCurrentBranch && (
              <button
                onClick={handleConfirmPayment}
                disabled={isConfirmingPayment}
                className={`h-10 px-3 py-1 bg-green-500 rounded-lg flex items-center gap-2 shadow-sm transition-shadow text-white flex-row-reverse ${
                  isConfirmingPayment ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-md'
                }`}
              >
                <span className="text-sm font-medium font-['IBM Plex Sans Arabic'] text-white">
                  {isConfirmingPayment
                    ? t('orders.confirming_payment')
                    : t('orders.confirm_payment')}
                </span>
                <Check className="w-5 h-5 text-white" />
              </button>
            )}

            {(shouldShowPartialPaymentButton() || canCancelDineInOrder) && (
              <button
                onClick={handleCancelOrder}
                className={`h-10 px-3 py-1 bg-red-50 rounded-lg flex items-center gap-2 shadow-sm hover:shadow-md transition-shadow flex-row-reverse`}
              >
                <span className="text-sm font-medium font-['IBM Plex Sans Arabic'] text-red-500">
                  {t('orders.cancel_order') || 'إلغاء الطلب'}
                </span>
                <img src={RedDeleteIcon} alt="cancel" className="w-5 h-5" />

              </button>
            )}
          </div>

        
        </div>
      </div>

      {/* Scrollable Content - Order Details Section */}
      <div className="flex-1 px-4 pb-4 overflow-y-auto min-h-0">
        {/* Order Details Section - Two Columns */}
        <div className="flex gap-4 items-start">
          {/* Right Column - Order Card and Additional Info */}
          <div className="flex-1 flex flex-col gap-4">
            {/* Order Card */}
            <div className="bg-white rounded-2xl p-4 flex flex-col gap-4">
              {/* Header: Status and Customer */}
              <div className={`flex justify-between items-center flex-row-reverse`}>
                {/* Status Badge */}
                <div className={`px-2 py-0.5 ${statusConfig.bgColor} rounded-lg flex items-center gap-1 flex-row-reverse`}>
                  <span className={`text-sm font-medium font-['IBM Plex Sans Arabic'] ${statusConfig.textColor}`}>
                    {statusConfig.label}
                  </span>
                  {typeof StatusIcon === 'string' ? (
                    <img src={StatusIcon} alt={statusConfig.label} className="w-3.5 h-3.5" />
                  ) : (
                    <StatusIcon className="w-3.5 h-3.5 text-black" />
                  )}
                </div>

                {/* Customer Info */}
                <div className={`flex-1 flex items-center gap-3 flex-row-reverse`}>
                  <div className={`flex-1 flex flex-col gap-1 items-start`}>
                    <div className={`text-base font-medium font-['IBM Plex Sans Arabic'] text-text-primary`}>
                      {orderDetail.user.name}
                    </div>
                    <div className={`text-sm font-normal font-['IBM Plex Sans Arabic'] text-text-secondary`}>
                      {orderDetail.user.phone}
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center overflow-hidden">
                    <img 
                      src={userIcon} 
                      alt="user" 
                      className=" object-cover" 
                    />       
                  </div>
                </div>
              </div>

              {/* Order Details Tags */}
              <div className={`flex items-start gap-2 flex-wrap justify-start`}>
                {orderDetails.map((detail, index) => (
                  <div
                    key={index}
                    className={`px-1.5 py-1 bg-neutral-100 rounded-lg flex items-center gap-2 flex-row-reverse`}
                  >
                    <span className={`text-xs font-normal font-['IBM Plex Sans Arabic'] text-text-primary`}>
                      {detail}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery Information - Show only when type_id === 1 (delivery) and no table */}
            {orderDetail.address && orderDetail.typeId === 1 && !orderDetail.table && (
              <div className="self-stretch p-4 bg-white rounded-2xl flex flex-col justify-between items-end gap-3">
                <div className="self-stretch text-right justify-start text-text-primary text-base font-medium font-['IBM Plex Sans Arabic'] leading-6">
                  {t('orders.delivery_details') || 'تفاصيل التوصيل'}
                </div>
                <div className="self-stretch flex flex-col justify-between items-start gap-2">
                  <div className="self-stretch inline-flex justify-between items-center gap-2">
                  
                    <div className=" justify-start text-text-tertiary text-sm font-normal font-['IBM Plex Sans Arabic'] leading-5">
                      {t('orders.address') || 'العنوان'}
                    </div>
                    <div className=" text-text-primary text-sm font-normal font-['Inter'] leading-5">
                      {orderDetail.address.descriptiveAddress || '-'}
                    </div>
                  </div>
                  <div className="self-stretch inline-flex justify-start items-center gap-1">
                  <div className="flex-1 text-right justify-start text-text-tertiary text-sm font-normal font-['IBM Plex Sans Arabic'] leading-5">
                      {t('orders.address_details') || 'تفاصيل العنوان'}
                    </div>
                    <div className="justify-start text-text-primary text-sm font-normal font-['Inter'] leading-5">
                      {orderDetail.address.addressName || '-'}
                    </div>
                 
                  </div>
                  <div className="self-stretch inline-flex justify-between items-center gap-1">
                  <div className="flex-1 text-right justify-start text-text-tertiary text-sm font-normal font-['IBM Plex Sans Arabic'] leading-5">
                      {t('orders.delivery_start_time') || 'بدا التوصيل'}
                    </div>
                    <div className="justify-start text-text-primary text-sm font-normal font-['Inter'] leading-5">
                      {orderDetail.deliveryStartTime || '-'}
                    </div>
             
                  </div>
                  <div className="self-stretch inline-flex justify-between items-center gap-1">
                  <div className="flex-1 text-right justify-start text-text-tertiary text-sm font-normal font-['IBM Plex Sans Arabic'] leading-5">
                      {t('orders.delivery_end_time') || 'انتهاء التوصيل'}
                    </div>
                    <div className="justify-start text-text-primary text-sm font-normal font-['Inter'] leading-5">
                      {orderDetail.deliveryEndTime || '-'}
                    </div>
                   
                  </div>
                  <div className="self-stretch inline-flex justify-between items-center gap-1">
                  <div className=" text-text-tertiary text-sm font-normal font-['IBM Plex Sans Arabic'] leading-5">
                      {t('orders.delivery_duration') || 'زمن الرحلة'}
                    </div>
                    <div className=" text-text-primary text-sm font-normal font-['Inter'] leading-5">
                      {orderDetail.deliveryDuration || '-'}
                    </div>
                    
                  </div>
                </div>
              </div>
            )}

            {/* Table Information - Show only when table exists and type_id !== 1 (not delivery) */}
            {orderDetail.table && orderDetail.typeId !== 1 && (
              <div className="self-stretch p-4 bg-white rounded-2xl flex flex-col justify-start items-end gap-3">
                <div className="self-stretch text-right justify-start text-text-primary text-base font-medium font-['IBM Plex Sans Arabic'] leading-6">
                  {t('orders.table_details') || 'تفاصيل الطاولة'}
                </div>
                <div className="self-stretch flex flex-col justify-start items-start gap-2">
                  <div className="self-stretch inline-flex justify-start items-center gap-2">
                    <div className="flex-1 flex justify-between items-center gap-1">
                    <div className=" justify-start text-text-tertiary text-sm font-normal font-['IBM Plex Sans Arabic'] leading-5">
                      {t('orders.table') || 'الطاولة'}
                    </div>
                      <div className=" justify-start text-text-primary text-sm font-medium font-['IBM Plex Sans Arabic'] leading-5">
                        {orderDetail.table.name}
                      </div>
                    </div>
                   
                  </div>
                  <div className="self-stretch inline-flex justify-between items-center gap-1">              
                    <div className="flex-1 text-right justify-start text-text-tertiary text-sm font-normal font-['IBM Plex Sans Arabic'] leading-5">
                      {t('orders.price') || 'السعر'}
                    </div>
                    <div className="size-5 p-1.5 flex justify-center items-center gap-1.5">
                    <div className=" justify-start text-text-primary text-sm font-medium font-['IBM Plex Sans Arabic'] leading-5">
                      {orderDetail.table.tablePrice}
                    </div>
                      <CurrencyDisplay className="" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Car Details Information - Show only when carDetails exists and type_id === 4 (car pickup) */}
            {orderDetail.carDetails && orderDetail.typeId === 4 && (
              <div className="self-stretch p-4 bg-white rounded-2xl flex flex-col justify-start items-end gap-3">
                <div className="self-stretch text-right justify-start text-text-primary text-base font-medium font-['IBM Plex Sans Arabic'] leading-6">
                  {t('orders.car_details') || 'تفاصيل السيارة'}
                </div>
                <div className="self-stretch flex flex-col justify-start items-start gap-2">
                  <div className="self-stretch inline-flex justify-start items-center gap-2">
                    <div className="flex-1 flex justify-between items-center gap-1">
                      <div className=" justify-start text-text-tertiary text-sm font-normal font-['IBM Plex Sans Arabic'] leading-5">
                        {t('orders.car_model') || 'نوع السيارة'}
                      </div>
                      <div className=" justify-start text-text-primary text-sm font-medium font-['IBM Plex Sans Arabic'] leading-5">
                        {orderDetail.carDetails.carModel || '-'}
                      </div>
                    </div>
                  </div>
                  <div className="self-stretch inline-flex justify-between items-center gap-1">
                    <div className="flex-1 text-right justify-start text-text-tertiary text-sm font-normal font-['IBM Plex Sans Arabic'] leading-5">
                      {t('orders.car_color') || 'لون السيارة'}
                    </div>
                    <div className=" justify-start text-text-primary text-sm font-medium font-['IBM Plex Sans Arabic'] leading-5">
                      {orderDetail.carDetails.carColor || '-'}
                    </div>
                  </div>
                  <div className="self-stretch inline-flex justify-between items-center gap-1">
                    <div className="flex-1 text-right justify-start text-text-tertiary text-sm font-normal font-['IBM Plex Sans Arabic'] leading-5">
                      {t('orders.car_number') || 'رقم السيارة'}
                    </div>
                    <div className=" justify-start text-text-primary text-sm font-medium font-['IBM Plex Sans Arabic'] leading-5">
                      {orderDetail.carDetails.carNumber || '-'}
                    </div>
                  </div>
                  {orderDetail.carDetails.carNotes && (
                    <div className="self-stretch inline-flex justify-between items-center gap-1">
                      <div className="flex-1 text-right justify-start text-text-tertiary text-sm font-normal font-['IBM Plex Sans Arabic'] leading-5">
                        {t('orders.car_notes') || 'ملاحظات السيارة'}
                      </div>
                      <div className=" justify-start text-text-primary text-sm font-medium font-['IBM Plex Sans Arabic'] leading-5">
                        {orderDetail.carDetails.carNotes}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Information */}
            {shouldShowPaymentInfo && (
              <div className="flex flex-col gap-3 max-h-[700px] overflow-y-auto">
                {orderDetail.partialPayments.length > 0 ? (
                  <>
                    {orderDetail.partialPayments.map((payment, index) => {
                      const statusInfo = getPaymentStatusIcon(payment)
                      return (
                        <div key={index} className="bg-white rounded-2xl p-5 flex flex-col gap-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-text-secondary">
                              {t('orders.paid_amount') || 'المبلغ المدفوع'}
                            </span>
                            <div className="flex items-center gap-1">
                              <span className="text-base font-medium font-['IBM Plex Sans Arabic'] text-text-primary">
                                {(Number(payment.amount) || 0).toFixed(2)}
                              </span>
                              <CurrencyDisplay className="w-4 h-4" />
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-text-secondary">
                              {t('orders.payment_method') || 'طريقة الدفع'}
                            </span>
                            <div className="flex items-center gap-1">
                              <img src={getPaymentMethodIcon(payment.paymentMethod)} alt="payment method" className="w-4 h-4" />
                              <span className="text-sm font-medium font-['IBM Plex Sans Arabic'] text-text-primary">
                                {payment.paymentMethodText}
                              </span>
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-text-secondary">
                              {t('orders.payment_status') || 'حالة الدفع'}
                            </span>
                            <div className="flex items-center gap-1">
                              <img 
                                src={statusInfo.icon} 
                                alt="status" 
                                className="w-4 h-4" 
                              />
                              <span className="text-sm font-medium font-['IBM Plex Sans Arabic']" style={{ color: statusInfo.color }}>
                                {getPaymentStatusText(payment)}
                              </span>
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-text-secondary">
                              {payment.id !== 0 ? (t('orders.payment_id') || 'رقم الدفع') : (t('orders.order_number') || 'رقم الطلب')}
                            </span>
                            <span className="text-sm font-medium font-['IBM Plex Sans Arabic'] text-text-primary">
                              {payment.id !== 0 ? payment.id : orderDetail.id}
                            </span>
                          </div>
                        </div>
                      )
                    })}

                    {/* Remaining Amount */}
                    {orderDetail.hasRealPartialPayments && orderDetail.remainingPaymentAmount > 0 && (
                      <div className="bg-white rounded-xl p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium font-['IBM Plex Sans Arabic'] text-gray-700">
                            {t('orders.remaining_amount') || 'المبلغ المتبقي'}
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-base font-semibold font-['IBM Plex Sans Arabic'] text-text-primary">
                              {(Number(orderDetail.remainingPaymentAmount) || 0).toFixed(2)}
                            </span>
                            <CurrencyDisplay className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-white rounded-2xl p-5 flex flex-col gap-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-text-secondary">
                        {t('orders.paid_amount') || 'المبلغ المدفوع'}
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="text-base font-medium font-['IBM Plex Sans Arabic'] text-text-primary">
                          {fallbackPayment.amount.toFixed(2)}
                        </span>
                        <CurrencyDisplay className="w-4 h-4" />
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-text-secondary">
                        {t('orders.payment_method') || 'طريقة الدفع'}
                      </span>
                      <div className="flex items-center gap-1">
                        <img src={getPaymentMethodIcon(fallbackPayment.paymentMethod)} alt="payment method" className="w-4 h-4" />
                        <span className="text-sm font-medium font-['IBM Plex Sans Arabic'] text-text-primary">
                          {fallbackPayment.paymentMethodText || fallbackPayment.paymentMethod || '-'}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-text-secondary">
                        {t('orders.payment_status') || 'حالة الدفع'}
                      </span>
                      <div className="flex items-center gap-1">
                        <img 
                          src={fallbackPaymentStatusInfo.icon} 
                          alt="status" 
                          className="w-4 h-4" 
                        />
                        <span className="text-sm font-medium font-['IBM Plex Sans Arabic']" style={{ color: fallbackPaymentStatusInfo.color }}>
                          {fallbackPaymentStatusText}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-text-secondary">
                        {paymentIdentifierLabel}
                      </span>
                      <span className="text-sm font-medium font-['IBM Plex Sans Arabic'] text-text-primary">
                        {paymentIdentifierValue}
                      </span>
                    </div>
                    {paymentDateInfo && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-text-secondary">
                          {t('orders.payment_date') || 'تاريخ الدفع'}
                        </span>
                        <div className="flex flex-col items-end">
                          <span className="text-sm font-medium font-['IBM Plex Sans Arabic'] text-text-primary">
                            {paymentDateInfo.date}
                          </span>
                          <span className="text-[11px] font-normal font-['IBM Plex Sans Arabic'] text-text-secondary">
                            {paymentDateInfo.time}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Left Column - Receipt Container */}
          <div className="flex-1 relative">
            <div className="bg-white rounded-t-2xl p-4 flex flex-col h-[580px]">
              {/* Items List */}
              <div className="flex-1 overflow-y-auto mb-4">
                {orderDetail.items.length === 0 ? (
                  <div className="flex flex-col justify-center items-center h-full">
                    <div className="text-6xl mb-4 text-gray-400">🛒</div>
                    <p className="text-lg font-medium font-['IBM Plex Sans Arabic'] text-gray-600">
                      {t('orders.no_items_in_order') || 'لا توجد أصناف في الطلب'}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {orderDetail.items.map((item, index) => (
                      <div key={index} className="flex flex-col gap-2">
                        {item.meals.map((meal, mealIndex) => (
                          <div key={mealIndex} className="flex justify-between items-start">
                            <div className="flex-1 flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <span className="text-base font-bold font-['IBM Plex Sans Arabic'] text-text-primary">
                                  {String(meal.name || '')}
                                </span>
                                <span className="px-2 py-1 bg-neutral-100 rounded text-sm font-medium font-['IBM Plex Sans Arabic']">
                                  x{item.quantity}
                                </span>
                              </div>
                              {meal.additionals.length > 0 && (
                                <div className="flex flex-row items-center gap-1 flex-wrap">
                                  {meal.additionals.map((add, addIndex) => (
                                    <React.Fragment key={addIndex}>
                                      <span className="text-xs font-normal font-['IBM Plex Sans Arabic'] text-text-secondary">
                                        {String(add.name || '')}
                                      </span>
                                      {addIndex < meal.additionals.length - 1 && (
                                        <span className="text-xs font-normal font-['IBM Plex Sans Arabic'] text-text-secondary">-</span>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-text-primary">
                                {(calculateItemTotal(item) || 0).toFixed(2)}
                              </span>
                              <CurrencyDisplay className="w-4 h-4" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="w-full h-px bg-gray-200 my-2 "></div>
              {/* Notes - Show only if notes exist */}
              {orderDetail.notes && orderDetail.notes.trim() !== '' && (
                <div className="flex flex-col gap-2 pb-2">
                  <span className="text-xs font-medium font-['IBM Plex Sans Arabic'] text-text-secondary">
                    {t('orders.order_notes') || t('common.notes') || 'ملاحظات'}
                  </span>
                  <div className="bg-white rounded-lg p-3 border border-neutral-200">
                    <p 
                      className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-text-primary leading-relaxed break-words whitespace-pre-wrap"
                      style={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        maxHeight: '120px',
                        overflowY: 'auto',
                      }}
                    >
                      {orderDetail.notes}
                    </p>
                  </div>
                </div>
              )}
              {/* Summary Section */}
              <div className="flex flex-col gap-3 pt-2">
             
             

                {/* Subtotal */}
                <div className="flex justify-between items-center">
                  <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-gray-600">
                    {t('orders.total_items') || 'اجمالي الاصناف'}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-gray-600">
                      {(Number(orderDetail.amount) || 0).toFixed(2)}
                    </span>
                    <CurrencyDisplay className="w-4 h-4 opacity-60" />
                  </div>
                </div>

                {/* Tax */}
                {orderDetail.taxValue > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-gray-600">
                      {t('orders.tax')} {orderDetail.taxPercentage ? `${orderDetail.taxPercentage}%` : ''}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-gray-600">
                        {(Number(orderDetail.taxValue) || 0).toFixed(2)}
                      </span>
                      <CurrencyDisplay className="w-4 h-4 opacity-60" />
                    </div>
                  </div>
                )}

                {(orderDetail.servicePrice > 0 || orderDetail.serviceFeePercentage != null) && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-gray-600">
                      {orderDetail.serviceFeePercentage != null
                        ? t('home.service_fee_with_percentage', { percentage: orderDetail.serviceFeePercentage })
                        : t('order.service_price')}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-gray-600">
                        {orderDetail.servicePrice.toFixed(2)}
                      </span>
                      <CurrencyDisplay className="w-4 h-4 opacity-60" />
                    </div>
                  </div>
                )}

                {/* Table Price - Show only if type_id is 3 (محلي), even if zero */}
                {orderDetail.table && 
                 orderDetail.typeId === 3 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-gray-600">
                      {t('order.table_price') || 'سعر الطاولة'}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-gray-600">
                        {orderDetail.table.tablePrice || '0.00'}
                      </span>
                      <CurrencyDisplay className="w-4 h-4 opacity-60" />
                    </div>
                  </div>
                )}

                {/* Delivery Price - Show only if type_id is 1 (تواصيل), even if zero */}
                {orderDetail.typeId === 1 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-gray-600">
                      {t('order.delivery_price') || 'سعر التوصيل'}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-gray-600">
                        {orderDetail.deliveryPrice ? parseFloat(orderDetail.deliveryPrice).toFixed(2) : '0.00'}
                      </span>
                      <CurrencyDisplay className="w-4 h-4 opacity-60" />
                    </div>
                  </div>
                )}

                {/* Promotion / coupon discount — under tax, before total */}
                {(() => {
                  const promotionDiscount = orderDetail.promotion?.discount ?? 0
                  const couponDiscount = orderDetail.discount
                    ? parseFloat(String(orderDetail.discount))
                    : 0
                  const discountAmount =
                    promotionDiscount > 0
                      ? promotionDiscount
                      : orderDetail.cobone && couponDiscount > 0
                        ? couponDiscount
                        : 0
                  if (discountAmount <= 0) return null
                  return (
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-gray-600">
                        {t('offers.benefitDiscount') || t('orders.discount') || 'الخصم'}
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-semibold font-['IBM Plex Sans Arabic'] text-green-600">
                          -{discountAmount.toFixed(2)}
                        </span>
                        <CurrencyDisplay
                          className="w-4 h-4"
                          iconStyle={{ filter: 'hue-rotate(120deg)' }}
                        />
                      </div>
                    </div>
                  )
                })()}

                {/* Cashback — display only; not subtracted from total */}
                {(orderDetail.promotion?.cashback ?? 0) > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-gray-600">
                      {t('offers.benefitCashback') || 'كاش باك'}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-semibold font-['IBM Plex Sans Arabic'] text-green-600">
                        {(orderDetail.promotion!.cashback ?? 0).toFixed(2)}
                      </span>
                      <CurrencyDisplay
                        className="w-4 h-4"
                        iconStyle={{ filter: 'hue-rotate(120deg)' }}
                      />
                    </div>
                  </div>
                )}

                {/* Set item product discount — subtracted from total */}
                {(orderDetail.promotion?.set_item_price_savings ??
                  orderDetail.promotion?.setItemPriceSavings ??
                  0) > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-normal font-['IBM Plex Sans Arabic'] text-gray-600">
                      {t('offers.benefitSetItemDiscount') || 'خصم على المنتج'}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-semibold font-['IBM Plex Sans Arabic'] text-green-600">
                        -
                        {(
                          orderDetail.promotion?.set_item_price_savings ??
                          orderDetail.promotion?.setItemPriceSavings ??
                          0
                        ).toFixed(2)}
                      </span>
                      <CurrencyDisplay
                        className="w-4 h-4"
                        iconStyle={{ filter: 'hue-rotate(120deg)' }}
                      />
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div className="w-full h-px bg-gray-200 my-2"></div>

                {/* Total */}
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium font-['IBM Plex Sans Arabic'] text-text-primary">
                    {t('orders.total') || 'الاجمالي'}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-medium font-['IBM Plex Sans Arabic'] text-text-primary">
                      {(Number(orderDetail.finalTotal) || 0).toFixed(2)}
                    </span>
                    <CurrencyDisplay className="w-5 h-5" />
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Circle Pattern */}
            <div className="absolute bottom-[-35px] left-0 right-0 h-[70px] flex justify-between items-end px-2">
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className="w-8 h-8 rounded-full bg-neutral-100"
                  style={{
                    marginBottom: '-35px',
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Order Modal */}
      <CancelOrderModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleConfirmCancel}
        isRTL={isRTL}
        isLoading={isCanceling}
      />

      <ConfirmPaymentModal
        isOpen={isConfirmPaymentModalOpen}
        isRTL={isRTL}
        isLoading={isConfirmingPayment}
        onClose={() => setIsConfirmPaymentModalOpen(false)}
        onConfirm={handleConfirmPaymentConfirm}
      />

      <EditOrderModal
        isOpen={isEditOrderModalOpen}
        orderId={orderDetail.id}
        order={orderDetail}
        isRTL={isRTL}
        onClose={() => setIsEditOrderModalOpen(false)}
        onSaved={refreshOrderDetail}
      />

      {/* Receipt Component - Only visible when printing */}
      {orderDetail && (
        <Receipt order={orderDetail} isRTL={isRTL} />
      )}
    </div>
  )
}

export default OrderDetailsPage


